'use strict';

const { bindGA, sendEventGA, normalizePayload } = require('./util/analytics-util');
const { queryAll, queryFirst, isVisible } = require('lilly/domUtil');
const {
    SELECT_PROMO, VIEW_PROMO,
    STICKY_GWP_DRAWER, STICKY_GWP_TOGGLE, STICKY_GWP_CARD, STICKY_GWP_CTA,
    STICKY_GWP_INTERACTION,
    STICKY_GWP_EXPAND, STICKY_GWP_COLLAPSE, STICKY_GWP_VIEW_COLLAPSED, STICKY_GWP_VIEW_EXPANDED
} = require('./util/constants');

const { utag_data: uData } = window;

/**
 * GA4 promotion fields for a card, read from its data attributes.
 * @param {HTMLElement} card - the .sticky-gwp-card element.
 * @param {HTMLElement} drawer - the drawer root (for card position).
 * @returns {Object} promo_id / promo_name / promo_creative / promo_position.
 */
function cardPromoData(card, drawer) {
    const { promoId, promoName, promoCreative } = card.dataset;
    const position = queryAll(STICKY_GWP_CARD, drawer).indexOf(card) + 1;

    return {
        promo_id: promoId ? [promoId] : [],
        promo_name: promoName ? [promoName] : [],
        promo_creative: promoCreative ? [promoCreative] : [],
        promo_position: position ? [position] : []
    };
}

/**
 * GA4 for the sitewide Sticky GWP promotions drawer.
 */
function initEvents() {
    const drawer = queryFirst(STICKY_GWP_DRAWER);
    if (!drawer) return;

    // Collapsed drawer impression (the bar is visible on load).
    sendEventGA(normalizePayload({
        event_name: STICKY_GWP_INTERACTION,
        interaction_type: STICKY_GWP_VIEW_COLLAPSED,
        logged_in_status: uData.user_loggedin_status
    }));

    // Card impressions: view_promotion the first time each card scrolls into view.
    // Cards live in the hidden panel, so this registers when the drawer opens.
    const cardObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
            if (entry.isIntersecting && isVisible(entry.target)) {
                sendEventGA(normalizePayload(Object.assign({
                    event_name: VIEW_PROMO,
                    logged_in_status: uData.user_loggedin_status
                }, cardPromoData(entry.target, drawer))));
                observer.unobserve(entry.target);
            }
        });
    }, { threshold: 0.05 });

    queryAll(STICKY_GWP_CARD, drawer).forEach(card => {
        if (card.dataset.gaImpressions !== 'true') {
            cardObserver.observe(card);
            card.dataset.gaImpressions = 'true'; // eslint-disable-line no-param-reassign
        }
    });

    // Expand / collapse drawer impression.
    bindGA(drawer, (bindElement, srcElement) => {
        const toggle = srcElement.closest(STICKY_GWP_TOGGLE);
        const expanding = !!toggle && toggle.getAttribute('aria-expanded') === 'false';

        if (expanding) {
            sendEventGA(normalizePayload({
                event_name: STICKY_GWP_INTERACTION,
                interaction_type: STICKY_GWP_VIEW_EXPANDED,
                logged_in_status: uData.user_loggedin_status
            }));
        }

        return {
            event_name: STICKY_GWP_INTERACTION,
            interaction_type: expanding ? STICKY_GWP_EXPAND : STICKY_GWP_COLLAPSE
        };
    }, {
        retriggerEvent: false,
        usesDataset: false,
        omitUADefaults: true,
        updatesUDO: false,
        bindOptions: { targetSelectors: [STICKY_GWP_TOGGLE] }
    });

    // Card CTA / link clicks (includes the Details link and Club Lilly join button).
    bindGA(drawer, (bindElement, srcElement) => {
        const card = srcElement.closest(STICKY_GWP_CARD);
        if (!card) return false;

        return Object.assign({
            event_name: SELECT_PROMO,
            click_text: (srcElement.textContent || '').trim(),
            click_url: srcElement.getAttribute('href') || ''
        }, cardPromoData(card, drawer));
    }, {
        retriggerEvent: false,
        usesDataset: false,
        omitUADefaults: true,
        updatesUDO: false,
        bindOptions: { targetSelectors: [STICKY_GWP_CTA] }
    });
}

initEvents();
